import React from 'react';
import { Award, Users, Heart, Mail, Phone } from 'lucide-react';
import { useCms, cmsImageUrl } from '../hooks/useCms';

const TEAM_DEFAULT = {
  title: 'Le persone dietro Assietica',
  subtitle: "Un team di professionisti che condivide i valori del Terzo Settore e dell'economia sociale. Persone che ogni giorno lavorano per proteggere chi genera impatto positivo sulla comunità.",
  delegati: [
    {
      nome: "Umberto Cossu",
      ruolo: "Delegato Assicurativo",
      descrizione: "Umberto Cossu nasce come volontario del soccorso sanitario, di protezione civile e di assistenza tecnico sportiva, un impegno che gli ha insegnato lucidità nelle emergenze, attenzione alle persone e senso profondo di responsabilità. Da questa esperienza prende forma il professionista: un consulente capace di comprendere davvero le esigenze di associazioni e cooperative, offrendo una competenza assicurativa costruita sul campo e orientata alla tutela concreta delle organizzazioni del Terzo Settore.",
      email: "umberto@assietica.it",
      foto: "https://customer-assets.emergentagent.com/job_03ee4f26-8936-402f-baec-16aca96234af/artifacts/dukhogve_WhatsApp%20Image%202026-02-26%20at%2018.25.38.jpeg"
    },
    {
      nome: "Mauro Antonio Mura",
      ruolo: "Delegato Assicurativo",
      descrizione: "Mauro Antonio Mura nasce dal volontariato a fianco delle persone con disabilità intellettiva, un impegno che gli ha trasmesso ascolto, attenzione ai dettagli e capacità di cogliere i bisogni reali delle persone e delle organizzazioni. Da questa sensibilità prende forma il professionista: un consulente che supporta enti del Terzo Settore e cooperative con competenze solide nella gestione del rischio e nella conformità normativa, offrendo soluzioni pensate per proteggere e rafforzare le loro attività.",
      email: "mauro@assietica.it",
      foto: "https://customer-assets.emergentagent.com/job_03ee4f26-8936-402f-baec-16aca96234af/artifacts/ta3cssnp_WhatsApp%20Image%202026-02-26%20at%2018.22.20.jpeg"
    }
  ],
  soci: [
    { nome: "Melania", ruolo: "Socia", foto: "" },
    { nome: "Marina", ruolo: "Socia", foto: "" },
    { nome: "Mariella", ruolo: "Socia", foto: "" }
  ],
};

const TeamSection = () => {
  const cms = useCms('team', TEAM_DEFAULT);
  const delegati = Array.isArray(cms.delegati) ? cms.delegati : TEAM_DEFAULT.delegati;
  const sociColleghi = Array.isArray(cms.soci) ? cms.soci : TEAM_DEFAULT.soci;
  const titleWords = (cms.title || '').split(' ');

  return (
    <section id="team" className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#0E8C84]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#1B2380]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="inline-block px-4 py-2 bg-[#1B2380]/10 text-[#1B2380] rounded-full text-sm font-semibold mb-4">
            Il Nostro Team
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#1B2380] mb-6">
            {titleWords.slice(0, -1).join(' ')}{' '}
            <span className="text-[#0E8C84]">{titleWords.slice(-1)}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {cms.subtitle}
          </p>
        </div>

        {/* Delegati Assicurativi */}
        <div className="mb-16">
          <div className="flex items-center justify-center mb-10">
            <div className="flex items-center space-x-3 bg-gradient-to-r from-[#1B2380]/10 to-[#0E8C84]/10 px-6 py-3 rounded-full">
              <Award className="text-[#1B2380]" size={24} />
              <h3 className="text-xl font-bold text-[#1B2380]">Delegati Assicurativi</h3>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {delegati.map((persona, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  {/* Foto Placeholder */}
                  <div className="w-32 h-32 bg-gradient-to-br from-[#1B2380] to-[#0E8C84] rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300 shadow-lg">
                    {persona.foto ? (
                      <img 
                        src={cmsImageUrl(persona.foto)} 
                        alt={persona.nome}
                        className="w-full h-full object-cover rounded-2xl"
                      />
                    ) : (
                      <span className="text-5xl text-white font-bold">
                        {persona.nome.split(' ').map(n => n[0]).join('')}
                      </span>
                    )}
                  </div>
                  
                  <div className="text-center sm:text-left">
                    <h4 className="text-2xl font-bold text-gray-900 mb-1 group-hover:text-[#1B2380] transition-colors duration-300">
                      {persona.nome}
                    </h4>
                    <p className="text-[#0E8C84] font-semibold mb-3">{persona.ruolo}</p>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {persona.descrizione}
                    </p>
                    <a 
                      href={`mailto:${persona.email}`}
                      className="inline-flex items-center space-x-2 text-[#1B2380] hover:text-[#0E8C84] transition-colors duration-300"
                    >
                      <Mail size={16} />
                      <span className="text-sm font-medium">{persona.email}</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Soci Colleghi */}
        <div>
          <div className="flex items-center justify-center mb-10">
            <div className="flex items-center space-x-3 bg-gradient-to-r from-[#0E8C84]/10 to-[#0E8C84]/10 px-6 py-3 rounded-full">
              <Users className="text-[#0E8C84]" size={24} />
              <h3 className="text-xl font-bold text-[#0E8C84]">I Nostri Soci</h3>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {sociColleghi.map((socio, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 text-center animate-fade-in-up"
                style={{ animationDelay: `${(index + 2) * 0.1}s` }}
              >
                {/* Foto Placeholder */}
                <div className="w-20 h-20 bg-gradient-to-br from-[#0E8C84] to-[#0E8C84] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                  {socio.foto ? (
                    <img 
                      src={cmsImageUrl(socio.foto)} 
                      alt={socio.nome}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <span className="text-2xl text-white font-bold">
                      {socio.nome[0]}
                    </span>
                  )}
                </div>
                <h4 className="text-lg font-bold text-gray-900 group-hover:text-[#0E8C84] transition-colors duration-300">
                  {socio.nome}
                </h4>
                <p className="text-gray-500 text-sm">{socio.ruolo}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team Values */}
        <div className="mt-16 bg-gradient-to-r from-[#1B2380] to-[#0E8C84] rounded-2xl p-8 sm:p-12 text-white shadow-2xl animate-fade-in-up">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="text-white" size={32} />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-6">
              Siamo persone che vivono il sociale ogni giorno
            </h3>
            <p className="text-lg text-white/90 leading-relaxed">
              Con i nostri valori cooperativi e benefit, proteggiamo ETS e Cooperative 
              con soluzioni su misura, perché chi genera impatto positivo merita una 
              tutela costruita attorno alla propria missione.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
